import { Country, State, type ICountry } from 'country-state-city';

export interface JurisdictionCoordinate {
  code: string;
  lat: number;
  lng: number;
  label: string;
}

const ISO_CODE_ALIASES: Record<string, string> = {};

const normalizeCode = (code: string): string =>
  code.trim().toUpperCase().replace(/\s+/g, '-').replace(/_/g, '-');

const tryParseNumber = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toCoordinate = (
  code: string,
  lat: number | null,
  lng: number | null,
  label: string
): JurisdictionCoordinate | null => {
  if (lat === null || lng === null) {
    return null;
  }
  return { code, lat, lng, label };
};

const getCountryLabel = (country?: ICountry | null) => country?.name ?? 'Unknown jurisdiction';

const ensureCountry = (code: string): ICountry | null =>
  Country.getCountryByCode(code) ?? null;

const resolveAliases = (code: string): string => ISO_CODE_ALIASES[code] ?? code;

const getStateCoordinate = (
  country: ICountry,
  subdivisionCode?: string
): JurisdictionCoordinate | null => {
  if (!subdivisionCode) return null;
  const state = State.getStateByCodeAndCountry(subdivisionCode, country.isoCode);
  if (!state) return null;
  const lat = tryParseNumber(state.latitude);
  const lng = tryParseNumber(state.longitude);

  return toCoordinate(
    `${country.isoCode}-${subdivisionCode}`,
    lat,
    lng,
    `${state.name}, ${country.name}`
  );
};

export const getCoordinatesForJurisdiction = (
  rawCode: string
): JurisdictionCoordinate | null => {
  if (!rawCode) return null;

  const normalized = normalizeCode(rawCode);
  const canonical = resolveAliases(normalized);

  const [countryCode, subdivisionCode] = canonical.split('-');
  const country = ensureCountry(countryCode);

  if (country) {
    const stateCoordinate = getStateCoordinate(country, subdivisionCode);
    if (stateCoordinate) {
      return stateCoordinate;
    }

    const lat = tryParseNumber(country.latitude);
    const lng = tryParseNumber(country.longitude);
    const label = getCountryLabel(country);

    const coordinate = toCoordinate(country.isoCode, lat, lng, label);
    if (coordinate) {
      return coordinate;
    }
  }

  console.warn(`No coordinates resolved for jurisdiction code: "${rawCode}"`);
  return null;
};

export const getCoordinatesForJurisdictions = (
  codes: string[]
): JurisdictionCoordinate[] =>
  codes
    .map((code) => getCoordinatesForJurisdiction(code))
    .filter((coord): coord is JurisdictionCoordinate => coord !== null);
